"use client";

import { useEffect, useState } from "react";

/**
 * Owns ONE concern: when the compose form's heavy body subtree is allowed to
 * mount.
 *
 * The compose body (editor, template pickers, recipient combos) is expensive
 * enough that mounting it in the same frame the modal opens visibly stutters the
 * open animation. `deferBody` pushes it one `requestAnimationFrame` past the
 * open so the shell animates alone.
 *
 * Inline surfaces (the Today/sequences composer pane) are always mounted and
 * have no animation to protect, so they pass `deferBody: false` and render
 * immediately.
 *
 * @param isOpen - whether the surface is open. Closing always hides the body.
 * @param deferBody - defer the body one frame after open instead of mounting it
 *   synchronously.
 * @returns whether the body subtree should render now.
 */
export function useDeferredComposeBody(isOpen: boolean, deferBody: boolean): boolean {
  const [showBody, setShowBody] = useState(!deferBody);

  useEffect(() => {
    if (!deferBody) {
      setShowBody(isOpen);
      return;
    }
    if (!isOpen) {
      setShowBody(false);
      return;
    }
    const id = requestAnimationFrame(() => setShowBody(true));
    return () => cancelAnimationFrame(id);
  }, [isOpen, deferBody]);

  return showBody;
}
